import { useLocation, Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, MessageCircle, Check } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import BookingForm from "@/components/BookingForm";
import FaqSection from "@/components/sections/FaqSection";
import { useCity } from "@/hooks/usePublicData";
import { useSite, telLink, waLink } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";

export default function CityPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/-taxi$/, "");
  const s = useSite();
  const { data: city, isLoading, isError } = useCity(slug);

  if (isError) return <NotFound />;
  if (isLoading || !city) return <div className="container-x py-24 text-center text-slate-400">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "TaxiService",
    name: `Chouhan Taxi Service — ${city.name}`, image: s.logo,
    url: `https://chouhantaxiservice.com/${slug}-taxi`,
    telephone: "+919982903736",
    address: { "@type": "PostalAddress", streetAddress: "Mehrangarh Fort Road", addressLocality: "Jodhpur", addressRegion: "Rajasthan", postalCode: "342024", addressCountry: "IN" },
    areaServed: city.name, serviceType: ["Local Taxi", "Airport Taxi", "Outstation Taxi", "Sightseeing Taxi"],
  };

  return (
    <>
      <Seo title={city.meta_title} description={city.meta_description} path={`/${slug}-taxi`} image={city.image} jsonLd={jsonLd} />
      <Breadcrumbs items={[{ label: `${city.name} Taxi` }]} />

      <section className="relative h-80 sm:h-96">
        <img src={city.image} alt={`Taxi service in ${city.name}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-x h-full flex flex-col justify-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4"><MapPin className="w-3.5 h-3.5 text-brand-sky" /> {city.name}, Rajasthan</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-tight">{city.h1}</h1>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-royal transition-colors">Book a Taxi <ArrowRight className="w-4 h-4" /></Link>
              <a href={telLink(s)} className="inline-flex items-center gap-2 bg-white text-navy font-semibold px-6 py-3 rounded-full"><Phone className="w-4 h-4" /> Call Now</a>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {city.intro?.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed text-lg mb-4">{p}</p>)}

            {city.sections?.map((sec, i) => (
              <div key={i} className="mt-8">
                <h2 className="font-heading font-bold text-navy-heading text-2xl mb-3">{sec.h2}</h2>
                <p className="text-slate-600 leading-relaxed">{sec.body}</p>
              </div>
            ))}

            {city.attractions?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-heading font-bold text-navy-heading text-2xl mb-4">Top Attractions in {city.name}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {city.attractions.map((a) => (
                    <div key={a} className="flex items-center gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100"><Check className="w-5 h-5 text-brand shrink-0" /><span className="text-sm text-slate-700 font-medium">{a}</span></div>
                  ))}
                </div>
              </div>
            )}

            {city.routes?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-heading font-bold text-navy-heading text-2xl mb-4">Popular Routes from {city.name}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {city.routes.map((r) => (
                    <Link key={r.slug} to={`/routes/${r.slug}`} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-brand transition-colors group">
                      <span className="text-sm font-medium text-navy-heading">{r.label}</span>
                      <ArrowRight className="w-4 h-4 text-brand group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-lg p-6">
              <h2 className="font-heading font-bold text-navy-heading text-lg mb-1">Book a Taxi in {city.name}</h2>
              <p className="text-xs text-slate-500 mb-4">Fast booking · we'll call to confirm</p>
              <BookingForm compact defaults={{ pickup_location: city.name, trip_type: "Local" }} />
            </div>
          </div>
        </div>
      </section>

      <FaqSection faqs={city.faqs || []} title={`${city.name} Taxi — FAQs`} subtitle={`Common questions about our taxi service in ${city.name}.`} />
    </>
  );
}
